import { useState, useEffect, useRef, useCallback } from 'react';
import { useCharacterCreationStore } from '../stores/characterCreationStore';
import { useCharacterCatalogStore } from '../stores/characterCatalogStore';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const AUTO_SAVE_DELAY_MS = 2000;
const SAVED_INDICATOR_DURATION_MS = 3000;

/**
 * Hook that auto-saves the character draft to the IndexedDB catalog.
 *
 * Features:
 * - Debounced saves (2 seconds after last change)
 * - Skips unnamed characters (nothing meaningful to save)
 * - Returns status for UI feedback
 * - Provides saveNow() for immediate saves
 *
 * @example
 * ```tsx
 * function CharacterBuilder() {
 *   const { status, saveNow } = useAutoSaveToCatalog();
 *   return (
 *     <>
 *       <SaveStatusIndicator status={status} />
 *       <button onClick={saveNow}>Save Now</button>
 *     </>
 *   );
 * }
 * ```
 */
export function useAutoSaveToCatalog() {
  const draft = useCharacterCreationStore((s) => s.draft);
  const { saveCharacterToCatalog } = useCharacterCatalogStore();

  const [status, setStatus] = useState<SaveStatus>('idle');
  const saveTimeoutRef = useRef<number>();
  const savedIndicatorTimeoutRef = useRef<number>();
  const lastSavedDraftRef = useRef<string>('');

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (savedIndicatorTimeoutRef.current) {
        clearTimeout(savedIndicatorTimeoutRef.current);
      }
    };
  }, []);

  // Perform the actual save
  const performSave = useCallback(async () => {
    // Skip if no name
    if (!draft.name?.trim()) {
      return;
    }

    // Skip if nothing changed since last save
    const draftJson = JSON.stringify(draft);
    if (draftJson === lastSavedDraftRef.current) {
      return;
    }

    setStatus('saving');

    try {
      await saveCharacterToCatalog(draft);
      lastSavedDraftRef.current = draftJson;
      setStatus('saved');

      // Clear the "saved" indicator after a few seconds
      if (savedIndicatorTimeoutRef.current) {
        clearTimeout(savedIndicatorTimeoutRef.current);
      }
      savedIndicatorTimeoutRef.current = window.setTimeout(() => {
        setStatus('idle');
      }, SAVED_INDICATOR_DURATION_MS);
    } catch (error) {
      console.error('Failed to save character to catalog:', error);
      setStatus('error');
    }
  }, [draft, saveCharacterToCatalog]);

  // Immediate save function for manual trigger
  const saveNow = useCallback(() => {
    // Clear any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    performSave();
  }, [performSave]);

  // Debounced auto-save on draft changes
  useEffect(() => {
    // Skip if character has no name (nothing meaningful to save)
    if (!draft.name?.trim()) {
      return;
    }

    // Skip if nothing changed
    const draftJson = JSON.stringify(draft);
    if (draftJson === lastSavedDraftRef.current) {
      return;
    }

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Schedule save after delay
    saveTimeoutRef.current = window.setTimeout(() => {
      performSave();
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [draft, performSave]);

  return { status, saveNow };
}
